import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, EMPTY, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take, map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

// Componentes
import { SessionExpiredDialogComponent } from '@app/shared/components/session-expired-dialog/session-expired-dialog.component';
import { ErrorModalComponent } from '@app/shared/components/error-modal/error-modal.component';

// Servicios
import { AuthService } from '../services/auth.service';
import { BrowserStorageService } from '../services/browser-storage.service';
import { LogoutService } from '@app/services/logout.service';
import { RefreshTokenService } from '@app/services/refresh-token.service';
import { EncryptionService } from '@app/services/encryption.service';
import { TranslocoService } from '@jsverse/transloco';
import { SILENT_BACKGROUND_READ } from '@app/core/http/silent-background-read.context';
import { SESSION_EXPIRY_NOT_EXCLUDED } from '@app/core/http/session-expiry-not-excluded.context';
import { ErrorCopyService } from '@app/shared/errors/error-copy.service';
import { parseBackendErrorBody } from '@app/domain/models/errors/backend-error.parser';
import { classifySessionError } from '@app/domain/models/errors/error-code.catalog';
import { readTotpLock } from '@app/domain/models/errors/totp-lock';
import { SesionTerminadaClave, SesionTerminadaService } from '@app/core/auth/sesion-terminada.service';
import { PREFIJO_PAYMENTS, esLecturaDePayments } from '@app/domain/auth/lectura-de-payments';

/** Cuerpo del refresco de token: solo se lee el access nuevo. */
interface RespuestaDeRefresco {
  readonly access: string;
}

@Injectable()
export class authInterceptor implements HttpInterceptor {
  private readonly LOGIN_ENDPOINT = '/api/users/auth/login/';
  private readonly VERIFY_2FA_ENDPOINT = '/api/users/auth/2fa/verify/';
  private readonly REFRESH_ENDPOINT = '/api/users/auth/token/refresh/';
  private readonly LOGOUT_ENDPOINT = '/api/users/auth/logout/';
  private readonly MASSIVE_TRANSFER_ENDPOINT = '/api/users/auth/massive-transfer/';
  private readonly TRANSFER_ENDPOINT = '/api/users/auth/transfer/';
  private readonly VERIFY_CODE_ENDPOINT = '/api/users/auth/2fa/verify-code/';
  private readonly NOTIFICATIONS_ENDPOINT = 'serial-focus-routes-ross.trycloudflare';
  private readonly BALANCES_PATTERN = '/balances/';
  private readonly INSTITUTIONS = 'CatalogoInstituciones'
  // Endpoint del módulo TPV consultado durante el login con enfoque a banca:
  // si la cuenta no es de TPV, regresa error 4xx esperado. Excluido del modal global.
  private readonly USER_ACCESS_TPV = '/user/access';
  private readonly USERS_ME_ENDPOINT = '/api/users/me';
  // Flujo de transferencia (individual y masiva): el modal de error lo maneja
  // el componente para no duplicar mensajes (uno del interceptor + uno del componente).
  private readonly REGISTER_ORDER_ENDPOINT = '/api_stp/any/v1/RegisterOrder';
  private readonly REGISTER_ORDER_BATCH_ENDPOINT = '/api_stp/any/v1/RegisterOrderBatch';
  private readonly VALIDATE_TRACKING_KEY_ENDPOINT = '/api_stp/any/v1/ValidarClaveRastreo';
  // Polipay Payments (`PAYMENTS_FRONT_CONTRATO.md`): links de cobro, cargos directos,
  // summary, encryption-key, verify-3ds, Cobros QR y lecturas LIVE. El manejo de error
  // queda en el componente (mensajes contextuales: tarjeta declinada, PCI 400, 3DS,
  // etc.). El prefijo cubre todos los sub-endpoints por `.includes()`.
  // ⚠️ Feature 52: el prefijo excluye del MODAL GLOBAL, pero de la RENOVACIÓN de sesión
  // solo a las escrituras (`esLecturaDePayments`). Las lecturas no llevan step-up ni
  // mueven dinero, y excluirlas se tragaba una sesión vencida real (el sondeo del QR
  // mostraba "Reconectando…" hasta el corte, sin avisar ni renovar).
  private readonly POLIPAY_PAYMENTS_ENDPOINT = PREFIJO_PAYMENTS;
  // Gestión de accesos (`GESTION_ACCESOS_FRONT.md`): el módulo maneja sus errores
  // contextuales (409 de alta, step-up TOTP §1.1 con reintento dentro del modal 2FA).
  // Sin la exclusión, el 401 de invalid_totp_code dispararía el modal global encima.
  private readonly GESTOR_ENDPOINT = '/api/gestor/';
  // Comisiones: MISMO motivo que gestores, y acá es peor. Definir una tarifa exige
  // segundo factor EN EL ENDPOINT, y el back rechaza un código malo con **401** y el
  // texto "Código de verificación inválido o expirado" — que contiene "expirado", así
  // que `isTokenExpiredError` lo leía como sesión vencida y abría el modal global:
  // un dígito mal tecleado deslogueaba al operador, o reintentaba la MISMA petición con
  // el MISMO código inválido. El módulo maneja sus tres casos adentro de la ventana del
  // código (`totp_required` / `invalid_totp_code` / `totp_validation_failed`).
  // ⚠️ Solo las dos rutas que ESCRIBEN. El prefijo `/api/commissions/` completo
  // cubriría también las lecturas (el listado de la pantalla y la calculadora de
  // Fondeo), que no llevan `totp_code` y por tanto nunca produjeron este falso
  // "expirado" — y excluirlas se tragaría una expiración de sesión REAL: en
  // Fondeo sin decir nada, porque su `catchError` cae a comisiones en cero.
  private readonly COMMISSIONS_BATCH_ENDPOINT = '/api/commissions/definition/batch/';
  private readonly COMMISSIONS_BULK_ENDPOINT = '/api/commissions/definition/bulk/';

  // Bloque de operación y matrices de comisiones TPV: su ausencia es ESPERADA
  // (404/400 en comercios y empresas recién creados) y cada componente ya la
  // maneja contextualmente (lecturas silenciosas, guardados con modal propio).
  // El modal global convertía esa ausencia en un "error" para el usuario.
  // Regex precompiladas; no se usa `/operation/` plano porque colisionaría con
  // los reportes legacy `/operation/transaction/...`.
  private readonly EXCLUDED_ENDPOINT_PATTERNS: RegExp[] = [
    /\/api\/v1\/tpv\/merchants\/[^/]+\/(operation|commissions)\//,
    /\/api\/v1\/tpv\/companies\/[^/]+\/(units\/[^/]+\/)?commissions\//,
    // ALTA de usuario: exige segundo factor EN EL ENDPOINT, igual que las
    // tarifas, y el back rechaza un código malo con **401** cuyo texto incluye
    // "expirado" → `isTokenExpiredError` lo leía como sesión vencida. Dos
    // síntomas, los dos reportados en vivo: (1) el diálogo "Sesión expirada"
    // aparecía encima de la ventana del código por un dígito mal tecleado; y
    // (2) al pulsar **Renovar** salía al login, porque el `catchError` de
    // `handle401Unauthorized` está DESPUÉS del `switchMap` que REINTENTA la
    // petición — y el reintento manda el MISMO código inválido, así que su 401
    // cae en ese `catchError` y dispara `processLogout()`. O sea que renovar la
    // sesión no podía funcionar nunca en este flujo.
    //
    // Va como REGEX y no como prefijo porque `.includes('…/persona/physical/')`
    // arrastraría también `…/physical/complete/`, que NO lleva segundo factor y
    // por tanto nunca produjo este falso "expirado": excluirla se tragaría una
    // expiración de sesión REAL (mismo criterio que las dos rutas de comisiones).
    // El `$` ancla el final: estas altas son POST sin query string.
    /\/api\/users\/onboarding\/persona\/(physical|empresarial|moral)\/(bulk\/)?$/,
    // EMISIÓN de una tarjeta virtual: exige step-up TOTP en el cuerpo
    // (`cards_issue_stepup_totp_error`) y su 401 de código malo trae el mismo
    // "…inválido o expirado". Solo la ruta que ESCRIBE: el resto de
    // `/api/cards/account-cards/<id>/…` son lecturas sin `totp_code` y excluirlas
    // se tragaría una expiración de sesión real. El panel pinta él mismo todos
    // los desenlaces (409 por riel, TOTP, y la escalera de `ErrorCopyService`).
    /\/api\/cards\/account-cards\/[^/]+\/issue-card\/$/,
    // CAMBIO DE ESTADO de una tarjeta: desbloquear y cancelar llevan step-up
    // TOTP (mismo 401 "…expirado"). Solo el PATCH `…/status/`; el directorio
    // `…/issued-cards/` y el detalle `…/issued-cards/<id>/` son lecturas y
    // conservan su modal de sesión vencida.
    /\/api\/cards\/(father-account|company|business-unit|account-cards)\/[^/]+\/issued-cards\/[^/]+\/status\/$/,
    // VINCULAR un centro de costos Transfer: step-up TOTP (mismo 401 "…expirado").
    // Es ruta de escritura ÚNICA, así que va aquí y no en la lista por método; los
    // candidatos (`…/transfer-ceco-candidates/`) son una LECTURA y conservan su
    // diálogo de sesión vencida.
    /\/api\/cards\/company\/link-transfer-ceco\/$/,
    // APROBAR / RECHAZAR pendientes SPEI (feature 87, R30): step-up TOTP en el
    // cuerpo, y el 401 de un código malo dice "…inválido o expirado", así que
    // sin esto un dígito mal tecleado abría el diálogo de sesión vencida (y al
    // cancelarlo, logout). Corrige también SPEI, que llama por el mismo router
    // montado en `/api/v1/stp/`; Cards llama por `/api_stp/any/v1/`. Rutas
    // solo-POST y de URL única: el `$` ancla el final y NO arrastra la lectura de
    // pendientes, el historial ni el detalle, que conservan su diálogo.
    /\/(api_stp\/any\/v1|api\/v1\/stp)\/AprobarTransaccion(Multiple)?\/$/,
    // DECIDIR / REINTENTAR solicitudes de aprobación de Cards (feature 107, R23):
    // step-up TOTP del aprobador, mismo 401 "…inválido o expirado". Cubre la
    // decisión individual (`…/apr_<id>/decision/`), el lote (`…/decision/`) y el
    // reintento (`…/apr_<id>/retry/`): rutas solo-POST de URL única, con `$`. La
    // lista y el contador (`…/approval-requests/`, `…/count/`) son LECTURAS y
    // conservan su diálogo de sesión vencida.
    /\/api\/cards\/approval-requests\/(apr_[^/]+\/)?(decision|retry)\/$/,
  ];

  // Exclusiones que dependen del MÉTODO, no solo de la ruta: la escritura y la
  // lectura comparten URL. El ALTA de una unidad de Cards (`POST
  // /api/cards/business-unit/`) lleva step-up TOTP y su 401 de código malo dice
  // "…inválido o expirado"; el GET del listado es la MISMA ruta y sin
  // `totp_code`, así que tiene que seguir abriendo el diálogo de sesión vencida.
  // El `$` ancla el final (el GET manda sus filtros como `params`, no en `url`).
  private readonly EXCLUDED_WRITE_ENDPOINTS: ReadonlyArray<{ readonly method: string; readonly pattern: RegExp }> = [
    { method: 'POST', pattern: /\/api\/cards\/business-unit\/$/ },
  ];

  // Lista de URLs donde NO queremos que aparezca el modal de error global
  private readonly EXCLUDED_ENDPOINTS = [
    this.LOGIN_ENDPOINT,
    this.VERIFY_2FA_ENDPOINT,
    this.LOGOUT_ENDPOINT,
    this.MASSIVE_TRANSFER_ENDPOINT,
    this.TRANSFER_ENDPOINT,
    this.VERIFY_CODE_ENDPOINT,
    this.NOTIFICATIONS_ENDPOINT,
    this.BALANCES_PATTERN,
    this.INSTITUTIONS,
    this.USER_ACCESS_TPV,
    this.USERS_ME_ENDPOINT,
    this.REGISTER_ORDER_ENDPOINT,
    this.REGISTER_ORDER_BATCH_ENDPOINT,
    this.VALIDATE_TRACKING_KEY_ENDPOINT,
    this.POLIPAY_PAYMENTS_ENDPOINT,
    this.GESTOR_ENDPOINT,
    this.COMMISSIONS_BATCH_ENDPOINT,
    this.COMMISSIONS_BULK_ENDPOINT
  ];

  // Código nuevo con `inject()`; el resto sigue en el constructor a propósito.
  private readonly errorCopy = inject(ErrorCopyService);
  // La reacción a una sesión terminada (un aviso + corte + login) vive fuera del
  // monolito; aquí solo se decide la rama y se delega (feature 33).
  private readonly sesionTerminada = inject(SesionTerminadaService);

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,
    private browserStorage: BrowserStorageService,
    private logout: LogoutService,
    private refresh: RefreshTokenService,
    private encryption: EncryptionService,
    private translocoService: TranslocoService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // 1. Omitir si tiene el header Skip-Auth
    if (request.headers.has('Skip-Auth-Interceptor')) {
      const cleanReq = request.clone({ headers: request.headers.delete('Skip-Auth-Interceptor') });
      return next.handle(cleanReq);
    }

    // 2. No interceptar la petición de Refresh
    if (request.url.includes(this.REFRESH_ENDPOINT)) {
      return next.handle(request);
    }

    const requestWithNgrok = this.addNgrokHeaderIfNeeded(request);
    return next.handle(this.addTokenFromStorage(requestWithNgrok)).pipe(
      catchError((error: HttpErrorResponse) => {
        // El rechazo TARDÍO con la sesión ya terminada no llega hasta acá: lo
        // absorbe `sesionTerminadaRechazoInterceptor`, que va por dentro (feature 33).
        // Verificamos si la URL actual está en nuestra lista de exclusión
        const isExcludedRequest = this.EXCLUDED_ENDPOINTS.some(url => request.url.includes(url))
          || this.EXCLUDED_ENDPOINT_PATTERNS.some(re => re.test(request.url))
          || this.EXCLUDED_WRITE_ENDPOINTS.some(e => e.method === request.method && e.pattern.test(request.url));

        // Marca por PETICIÓN (`HttpContext`), no por ruta: la puso quien construyó
        // la lectura porque nadie la espera en pantalla y ya tiene su "sin dato".
        // Se suma SOLO al argumento de `handleGlobalErrors`, nunca a
        // `isExcludedRequest`: plegarla ahí contaminaría la condición del 401 de
        // token expirado y una lectura de fondo dejaría de disparar el refresh,
        // rompiendo la sesión en silencio.
        const isSilentBackgroundRead = request.context.get(SILENT_BACKGROUND_READ);

        // Marca POR PETICIÓN (la pone el origen): "no llevo `totp_code`, así que mi
        // 401 de token expirado ES una sesión vencida aunque mi ruta esté excluida".
        // Solo abre la rama de sesión de abajo; NO se pliega en `isExcludedRequest`,
        // que también decide el modal genérico (lo abriría en un 5xx de `/balances/`).
        const sessionExpiryNotExcluded = request.context.get(SESSION_EXPIRY_NOT_EXCLUDED);

        // Manejo de Sesión Simultánea (403 en Login)
        if (error.status === 403 && request.url.includes(this.LOGIN_ENDPOINT)) {
          return this.handle403Conflict(request, next);
        }

        // Manejo de sesión inválida por múltiples inicios (401 específico)
        if (error.status === 401 && this.isMultipleSessionError(error)) {
          return this.handleSessionInvalidated();
        }

        // Cuenta deshabilitada (401 `USER_DISABLED`): logout con copy propio. Como
        // la rama de arriba, NO está condicionada a la lista de exclusión.
        if (error.status === 401 && this.isUserDisabledError(error)) {
          return this.handleSessionInvalidated('errors.session.userDisabled');
        }

        // Feature 52 (dev): el prefijo de payments se excluyó por el modal GLOBAL (cada
        // pantalla pinta su mensaje), no por la sesión. Sus LECTURAS sí renuevan; sus
        // escrituras siguen fuera: el reintento tras renovar cierra la sesión si falla (un
        // 409 esperado al cancelar un QR deslogearía) y un step-up responde 401 "expirado".
        const excluidaDeRenovacion = isExcludedRequest && !esLecturaDePayments(request.method, request.url);

        // Manejo de Token Expirado (401 específico) - Solo si NO es una ruta excluida
        // de la renovación, o si la petición trae `SESSION_EXPIRY_NOT_EXCLUDED` (feature 81).
        if (
          error.status === 401 &&
          (!excluidaDeRenovacion || sessionExpiryNotExcluded) &&
          this.isTokenExpiredError(error)
        ) {
          return this.handle401Unauthorized(request, next);
        }

        // El bloqueo del 2FA por intentos (429 `TOTP_LOCKED`) NO abre el modal
        // genérico: taparía la ventana del código, que es donde se explica
        // (lo anuncia `TotpLockInterceptor` y lo pinta `<app-two-factor-auth>`).
        // Es la misma clase de excepción que `EXCLUDED_ENDPOINTS`, decidida por
        // el CÓDIGO del cuerpo y no por la ruta: el bloqueo alcanza a TODAS.
        const isTotpLock = readTotpLock(error.status, error.error, null) !== null;

        // Otros errores (Pasamos la bandera de exclusión y la URL para clasificación fina)
        return this.handleGlobalErrors(
          error,
          isExcludedRequest || isSilentBackgroundRead || isTotpLock,
          request.url,
        );
      })
    );
  }

  private handleGlobalErrors(error: HttpErrorResponse, isExcluded: boolean, requestUrl: string): Observable<never> {
    // Si la ruta está excluida (Login, 2FA, etc.), dejamos que el componente maneje el error
    if (isExcluded) {
      return throwError(() => error);
    }

    // 404 del GET de comisiones: parche por URL CONSERVADO como peldaño 2 (lo
    // retira la feature 10). El `error_code` gana; esto sólo actúa sin código.
    const statusFallbackKey = error.status === 404 && /\/commissions\/?$/.test(requestUrl)
      ? 'tpv.customerDetail.commissionsNotRegistered'
      : undefined;

    // Escalera: código conocido -> status -> genérico. Nunca texto del backend.
    const resolved = this.errorCopy.resolve(error, { statusFallbackKey });

    this.dialog.open(ErrorModalComponent, {
      width: '400px',
      data: { message: resolved.text, traceId: resolved.traceId }
    });

    return throwError(() => error);
  }

  /** Decisión de sesión (§8): por `error_code` CON fallback a las subcadenas en
   *  español, que NO se borran. La lógica vive en `classifySessionError()`. */
  private sessionAction(error: HttpErrorResponse) {
    return classifySessionError(parseBackendErrorBody(error.error, null));
  }

  /** Expiración por inactividad: es la única que SÍ intenta renovar el token. */
  private isTokenExpiredError(error: HttpErrorResponse): boolean {
    return this.sessionAction(error) === 'refresh';
  }

  /** Sesión múltiple / token revocado: modal + logout, sin intentar refresh. */
  private isMultipleSessionError(error: HttpErrorResponse): boolean {
    return this.sessionAction(error) === 'invalidate';
  }

  /** Cuenta deshabilitada por un administrador: logout con copy propio. */
  private isUserDisabledError(error: HttpErrorResponse): boolean {
    return this.sessionAction(error) === 'disabled';
  }

  /** Manejo específico cuando el backend da la sesión por terminada */
  private handleSessionInvalidated(
    messageKey: SesionTerminadaClave = 'errors.session.invalidated',
  ): Observable<never> {
    return this.sesionTerminada.reaccionar(messageKey, () => this.processLogout());
  }

  /** Evita la página intersticial de Ngrok que devuelve 200 con HTML en vez del JSON del API. */
  private addNgrokHeaderIfNeeded(request: HttpRequest<any>): HttpRequest<any> {
    if (request.url.includes('ngrok')) {
      return request.clone({
        setHeaders: { 'ngrok-skip-browser-warning': '1' }
      });
    }
    return request;
  }

  private addTokenFromStorage(request: HttpRequest<any>): HttpRequest<any> {
    const encryptedAccess = this.browserStorage.getSessionStorageItem('access');
    let accessToken: string | null = null;
    try {
      if (encryptedAccess) accessToken = this.encryption.decrypt(encryptedAccess) || null;
    } catch { return request; }
    return accessToken ? request.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } }) : request;
  }

  private handle401Unauthorized(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // ⚠️ Se suscribe AQUÍ (mismo criterio que SesionTerminadaService, feature 33). Si
      // colgara de la petición dueña, un llamador que se des-suscribe —el tope por consulta
      // del sondeo del QR, un switchMap, una pantalla destruida— se llevaría el "Renovar"
      // con él: `isRefreshing` quedaba en true y toda petición posterior esperaba para
      // siempre (feature 52, R7). Termina sola: `afterClosed()` emite una vez y completa,
      // y el refresco es una sola petición. `refCount: false` para que la fuente se
      // suscriba UNA vez y siga viva aunque la dueña se vaya.
      const renovacion$ = this.renovarSesion().pipe(shareReplay({ bufferSize: 1, refCount: false }));
      // Su desenlace (logout incluido) ya lo maneja `renovarSesion`; acá solo se absorbe.
      renovacion$.subscribe({ error: () => undefined });

      return renovacion$.pipe(
        switchMap((newAccess) =>
          next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${newAccess}` } })).pipe(
            // Un reintento fallido de la petición dueña cierra la sesión, como antes. Una
            // dueña cancelada no llega hasta acá.
            // ⚠️ Excepción (feature 52, R16): en una LECTURA de payments solo un 401 cierra
            // la sesión. Sus 403/404/400/429/503 son estados normales de pantalla —p. ej.
            // `GET qr/?limit=1` → 503 PAYMENTS_QR_DISABLED en un ambiente sin QR, o un 403
            // sin permiso— y deslogueaban al operador justo después de pulsar "Renovar".
            catchError((err: unknown) => {
              const esLectura = esLecturaDePayments(request.method, request.url);
              if (!esLectura || (err instanceof HttpErrorResponse && err.status === 401)) {
                this.processLogout();
              }
              return throwError(() => err);
            })
          )
        )
      );
    } else {
      return this.refreshTokenSubject.pipe(filter(token => token !== null), take(1), 
        switchMap((token) => next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))));
    }
  }

  /** Diálogo → (Renovar) refresco + guardado + liberar a las que esperan | (Cerrar) logout. */
  private renovarSesion(): Observable<string> {
    const dialogRef = this.dialog.open(SessionExpiredDialogComponent, {
      width: '350px',
      disableClose: true,
      data: { title: this.translocoService.translate('errors.session.expiredTitle'), message: this.translocoService.translate('errors.session.expiredMessage') }
    });

    return dialogRef.afterClosed().pipe(
      switchMap((confirmed: boolean) => {
        if (!confirmed) {
          this.isRefreshing = false;
          this.processLogout();
          return EMPTY;
        }
        const refreshToken = sessionStorage.getItem('refresh');
        if (!refreshToken) { this.processLogout(); return EMPTY; }
        const encryptRefresh = this.encryption.decrypt(String(refreshToken));
        // `refreshAccessToken` declara `Observable<string>` pero emite el cuerpo del
        // POST (`{ access }`); se lee como `unknown` y se acota a esa forma.
        return this.refresh.refreshAccessToken(encryptRefresh).pipe(
          map((response: unknown) => {
            const newAccess = (response as RespuestaDeRefresco).access;
            this.browserStorage.setSessionStorageItem('access', this.encryption.encrypt(newAccess));
            this.isRefreshing = false;
            this.refreshTokenSubject.next(newAccess);
            return newAccess;
          }),
          catchError((err) => { this.isRefreshing = false; this.processLogout(); return throwError(() => err); })
        );
      })
    );
  }

  private handle403Conflict(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const credentials = this.authService.getPendingLoginCredentials();
    if (!credentials) return EMPTY;
    return this.authService.forceLogout({ email: credentials.emailOrPhone, password: credentials.password }).pipe(
      switchMap(() => {
        this.authService.triggerClearLoginForm();
        this.authService.clearPendingLoginCredentials();
        return next.handle(request);
      })
    );
  }

  private processLogout() {
    this.logout.logout();
    this.browserStorage.clearSessionStorage();
    sessionStorage.clear();
    window.location.assign('/login');
  }
}